import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import api from '@/admin/lib/api'
import type { ProductFilterFacet } from '@/shared/components/ui/product-filter-menu'

/**
 * Notification infrastructure - admin data layer.
 *
 * One React Query surface over the generic `/api/notifications/` endpoints.
 * Every consumer surface (the bell, a claim-detail inline panel, the claims
 * list attention dot) reads from this hook so read-state stays consistent.
 */

export type NotificationCategory =
  | 'customer_message'
  | 'action_required'
  | 'assignment'
  | 'mention'
  | 'system'

export type NotificationLevel = 'info' | 'attention' | 'critical'

export type NotificationStatus = 'unread' | 'read' | 'archived'

export interface NotificationAction {
  key: string
  label: string
  url?: string
  style?: string
}

export interface NotificationRow {
  uuid: string
  category: NotificationCategory
  level: NotificationLevel
  title: string
  body: string
  record_type: string
  record_uuid: string | null
  link_url: string
  actions: NotificationAction[]
  context: Record<string, unknown>
  status: NotificationStatus
  read_at: string | null
  archived_at: string | null
  created_at: string
  /** When the event last happened - a repeat on a deduped row moves it on. */
  occurred_at?: string
  /** Events folded into this row since it was last read. */
  event_count?: number
  actor_name: string
  /** Current owner of the record, so an alert row can show who should action it. */
  assignee_name?: string
  /** True when the owner is the reader - render "You" rather than their own name. */
  assignee_is_viewer?: boolean
  /** The record's customer, for claim notifications. */
  customer_name?: string
  /** How staff refer to the record (e.g. the claim number). */
  record_reference?: string
}

/**
 * What the record's inline "View" affordance should open for a notification.
 *
 * Driven by the action the backend attached to the notification, never by
 * `context.kind`: every documents/evidence producer emits a `view_evidence`
 * action (the consumer evidence-add path AND the tokenised external-upload
 * finalise path), while message producers emit a plain `view`. Matching on
 * `kind === 'evidence'` recognised only the first of those, so an
 * external-upload notification rendered a dead, greyed-out button (TA-22).
 *
 * `null` means there is nothing to open - callers render no button rather than
 * a disabled one.
 */
export function resolveNotificationViewTarget(
  notification: Pick<NotificationRow, 'actions' | 'category'>,
  options: { hasMessageToOpen: boolean },
): 'evidence' | 'message' | null {
  if (notification.actions?.some((action) => action.key === 'view_evidence')) {
    return 'evidence'
  }
  if (notification.category === 'customer_message' && options.hasMessageToOpen) {
    return 'message'
  }
  return null
}

export interface NotificationListParams {
  status?: NotificationStatus
  category?: NotificationCategory
  record_type?: string
  record_uuid?: string
}

const NOTIFICATIONS_KEY = 'notifications'
const UNREAD_COUNT_KEY = 'notifications-unread-count'

/**
 * Feed query params: the list scope plus the fly-out facet keys (comma-joined
 * values), ``search`` and ``ordering``. Facet keys come from the server.
 */
export type NotificationFeedParams = Record<string, string>

export interface NotificationSortOption {
  value: string
  label: string
}

export interface NotificationFeedPage {
  results: NotificationRow[]
  count: number
  /** Facets for the filter fly-out - first page only. */
  filter_options?: ProductFilterFacet[]
  sort_options?: NotificationSortOption[]
}

/**
 * The dashboard's notification feed, one page at a time ("Load more").
 * Shares the ``notifications`` key prefix so marking anything read refreshes
 * it; the previous result stays on screen while a new filter loads.
 */
export function useNotificationFeed(params: NotificationFeedParams) {
  return useInfiniteQuery({
    queryKey: [NOTIFICATIONS_KEY, 'feed', params],
    queryFn: ({ pageParam }): Promise<NotificationFeedPage> =>
      api
        .get('/notifications/', { params: { ...params, page: pageParam } })
        .then((r) => r.data),
    initialPageParam: 1,
    getNextPageParam: (last: NotificationFeedPage, pages: NotificationFeedPage[]) => {
      const loaded = pages.reduce((n, page) => n + page.results.length, 0)
      return loaded < last.count ? pages.length + 1 : undefined
    },
    placeholderData: keepPreviousData,
  })
}

export function useNotifications(
  params?: NotificationListParams,
  options?: { enabled?: boolean },
) {
  return useQuery<{ results: NotificationRow[]; count: number }>({
    queryKey: [NOTIFICATIONS_KEY, params ?? {}],
    queryFn: () => api.get('/notifications/', { params }).then((r) => r.data),
    enabled: options?.enabled ?? true,
  })
}

export function useUnreadNotificationCount() {
  return useQuery<{ count: number }>({
    queryKey: [UNREAD_COUNT_KEY],
    queryFn: () => api.get('/notifications/unread-count/').then((r) => r.data),
    // Cheap poll so the bell badge stays live without a socket.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })
}

function useInvalidateNotifications() {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: [NOTIFICATIONS_KEY] })
    qc.invalidateQueries({ queryKey: [UNREAD_COUNT_KEY] })
  }
}

/**
 * Bulk / scoped mark-read. Pass ``uuids`` to clear specific rows, or
 * ``record_type`` + ``record_uuid`` to clear every unread notification for a
 * record (the "I opened this record" path - the server-side replacement for
 * the old per-browser localStorage marker).
 */
export function useMarkNotificationsRead() {
  const invalidate = useInvalidateNotifications()
  return useMutation<
    { updated: number },
    Error,
    { uuids?: string[]; record_type?: string; record_uuid?: string; category?: string }
  >({
    mutationFn: (body) =>
      api.post('/notifications/mark-read/', body).then((r) => r.data),
    onSuccess: invalidate,
  })
}

export function useMarkAllNotificationsRead() {
  const invalidate = useInvalidateNotifications()
  return useMutation<{ updated: number }, Error, void>({
    mutationFn: () =>
      api.post('/notifications/mark-all-read/').then((r) => r.data),
    onSuccess: invalidate,
  })
}

export function useNotificationItemAction() {
  const invalidate = useInvalidateNotifications()
  return useMutation<
    unknown,
    Error,
    { uuid: string; action: 'read' | 'unread' | 'archive' }
  >({
    mutationFn: ({ uuid, action }) =>
      api.post(`/notifications/${uuid}/${action}/`).then((r) => r.data),
    onSuccess: invalidate,
  })
}
